import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import '../styles/NotFoundPage.css';

const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png';
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png';

const Star = ({ style }) => <span className="nf-star" style={style} />;

const NotFoundPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const btnRef = useRef(null);
  const [goingHome, setGoingHome] = useState(false);

  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;

  // Generate twinkling stars once per mount
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 60; i++) {
      arr.push({
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        width: `${1 + Math.random() * 2}px`,
        height: `${1 + Math.random() * 2}px`,
        opacity: 0.3 + Math.random() * 0.7,
        animationDelay: `${Math.random() * 4}s`,
        animationDuration: `${2 + Math.random() * 3}s`,
      });
    }
    return arr;
  }, []);

  // Parallax tilt effect on mouse move (desktop only)
  useEffect(() => {
    const digits = document.querySelector('.nf-digits');
    if (!digits) return;
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      digits.style.transform = `perspective(1000px) rotateY(${x * 6}deg) rotateX(${-y * 6}deg)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleHome = () => {
    setGoingHome(true);
    setTimeout(() => navigate('/'), 600);
  };

  return (
    <div className={`notfound-page ${goingHome ? 'nf-leaving' : ''}`} data-testid="notfound-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <title>404 Not Found — Registan Tourism</title>
      </Helmet>

      {/* Starfield */}
      <div className="nf-stars" aria-hidden="true">
        {stars.map((s, i) => <Star key={i} style={s} />)}
      </div>

      {/* Aurora glow blobs */}
      <div className="nf-aurora nf-aurora-1" aria-hidden="true" />
      <div className="nf-aurora nf-aurora-2" aria-hidden="true" />

      {/* Paper plane looping around digits */}
      <div className="nf-plane-orbit" aria-hidden="true">
        <svg className="nf-plane" viewBox="0 0 24 24" fill="none">
          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="nf-trail" />
      </div>

      <div className="notfound-content">
        <img
          src={currentLogo}
          alt="Registan Tourism"
          className="notfound-logo"
          onClick={() => navigate('/')}
          data-testid="notfound-logo"
        />

        <div className="nf-digits" aria-label="404">
          <span className="nf-digit nf-digit-4">4</span>
          <span className="nf-digit nf-digit-0">
            <span className="nf-zero-inner">0</span>
          </span>
          <span className="nf-digit nf-digit-4b">4</span>
        </div>

        <h1 className="notfound-title">{t('notFoundTitle') || 'Looks like you\'re off the map'}</h1>
        <p className="notfound-text">
          {t('notFoundText') || 'The page you\'re looking for drifted into the desert. Let\'s get you back on route.'}
        </p>

        <button
          ref={btnRef}
          className="notfound-btn"
          onClick={handleHome}
          data-testid="go-home-btn"
        >
          <span className="nf-btn-label">{t('notFoundBtn') || 'Take me home'}</span>
          <span className="nf-btn-arrow" aria-hidden="true">→</span>
          <span className="nf-btn-shine" aria-hidden="true" />
        </button>

        <div className="nf-suggest">
          <span className="nf-suggest-label">{t('notFoundTryLabel') || 'Or try:'}</span>
          <button className="nf-suggest-link" onClick={() => navigate('/tours')}>{t('tours') || 'Tours'}</button>
          <span className="nf-dot">·</span>
          <button className="nf-suggest-link" onClick={() => navigate('/visa-status')}>{t('visaStatus') || 'Visa Status'}</button>
          <span className="nf-dot">·</span>
          <button className="nf-suggest-link" onClick={() => navigate('/faq')}>{t('faq') || 'FAQ'}</button>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
